import { getDeviceId } from '@zep/session-id';

const AUTH_APP_URL = process.env.NEXT_PUBLIC_AUTH_APP_URL;

export type AuthAppService = 'quiz' | 'quiz-admin';

/**
 * auth-app 로그인 페이지 URL을 생성합니다.
 * locale을 URL 경로에 포함하여 auth-app에서도 동일 언어로 표시되도록 합니다.
 *
 * deviceId를 query로 전달하면 auth-app이 logger state로 주입하여
 * 모든 auth-app 이벤트가 quiz-app의 zepDeviceId와 같은 키로 조인 가능해집니다.
 *
 * @example
 * buildAuthAppSigninUrl('en') → 'https://auth.zep.us/en/signin?service=quiz&deviceId=...'
 * buildAuthAppSigninUrl('ko', '/my-page') → 'https://auth.zep.us/ko/signin?service=quiz&deviceId=...&callbackUrl=%2Fmy-page'
 */
export function buildAuthAppSigninUrl(
  locale: string,
  callbackUrl?: string,
  service: AuthAppService = 'quiz',
): string {
  const authUrl = new URL(`${AUTH_APP_URL}/${locale}/signin`);
  authUrl.searchParams.set('service', service);

  const deviceId = getDeviceId();
  if (deviceId) {
    authUrl.searchParams.set('deviceId', deviceId);
  }

  if (callbackUrl) {
    authUrl.searchParams.set('callbackUrl', callbackUrl);
  }
  // origin 을 넘겨야 auth-app 이 로그인 후 "호출한 그 주소"로 복귀시킨다.
  // - LOC: 로컬 개발 시 dev auth 서버가 localhost 로 redirect
  // - Vercel Preview(*.preview.zep.us): 프리뷰로 복귀 (auth-app 이 isAllowedPreviewOrigin 으로 검증)
  // - 셀프호스팅(EKS) dev-N-quiz-front.zep.us / live-quiz-front.zep.us: Vercel 이 아닌
  //   자체 오리진이라 preview 패턴에 안 걸린다 → 명시적으로 origin 을 넘겨야 서비스 기본
  //   URL(dev-quiz.zep.us / quiz.zep.us)로 튕기지 않고 이 호스트로 복귀한다.
  //   ⚠️ auth-app 의 isAllowedQuizSelfhostOrigin 과 **짝**이다. 여기만 고치면 origin 은
  //      붙지만 auth-app 이 거부해 서비스 기본 URL 로 튕긴다 — 양쪽을 같이 바꿔야 한다.
  //      live 는 PRD 에서도 허용된다(auth-app 의 QUIZ_SELFHOST_PROD_ORIGIN_HOST).
  //   컷오버 완료 후 서비스가 quiz.zep.us 로 나가면 이 분기 자체가 불필요해진다 —
  //   브라우저가 보는 호스트가 quiz.zep.us 라 auth 가 호스트 변경을 인지하지 못한다.
  // 그 외(dev/stage/prod)는 origin 미전달 → 서비스 기본 URL 로 복귀
  if (typeof window !== 'undefined') {
    const isLoc = process.env.NEXT_PUBLIC_STAGE === 'LOC';
    const isPreview = /\.preview\.zep\.us$/.test(window.location.hostname);
    const isSelfhost = /^(dev-\d+|live)-quiz-front\.zep\.us$/.test(
      window.location.hostname,
    );
    if (isLoc || isPreview || isSelfhost) {
      authUrl.searchParams.set('origin', window.location.origin);
    }
  }
  return authUrl.toString();
}
